<template>
  <div>
    <div class="w-80">
      <NLoading v-if="isLoadingSymbol" class="text-brand-primary" size="10" />

      <div v-else class="flex justify-between items-baseline p-3">
        <div class="flex flex-col">
          <div class="inline-flex items-center">
            <SymbolTradeStateIcon
              class="me-2"
              :trading-state-code="symbol.instrumentTradingState"
            />

            <Retail :isin="getSymbolIsin(symbol)">
              <NText variant="h3" tag="h3" weight="bold">
                {{ symbol.instrumentName }}
              </NText>
            </Retail>
          </div>

          <NText variant="caption1" class="text-neutral-secondary">
            {{ symbol.instrumentTitle }}
          </NText>
        </div>

        <div class="flex flex-col justify-around">
          <div class="inline-flex items-center">
            <NumberFormatter
              #="{ number }"
              :value="(symbol.referencePriceVariationPercentage ?? 0) / 100"
              maximum-fraction-digits="2"
              sign-display="always"
              number-style="percent"
            >
              <NText
                weight="bold"
                dir="ltr"
                class="mx-1"
                :class="{
                  'text-brand-primary':
                    (symbol.referencePriceVariationPercentage ?? 0) > 0,
                  'text-critical-primary':
                    (symbol.referencePriceVariationPercentage ?? 0) < 0,
                }"
              >
                ({{ number }})
              </NText>
            </NumberFormatter>

            <NumberFormatter #="{ number }" :value="symbol.lastTradePrice">
              <ChangePriceEffect :price="symbol.lastTradePrice ?? 0">
                <NText :key="number" variant="h1" tag="h1" weight="bold">
                  {{ number }}
                </NText>
              </ChangePriceEffect>
            </NumberFormatter>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-2 px-4 bg-neutral-secondary rounded-md">
      <div class="flex justify-between items-center py-3">
        <NText variant="caption1" weight="medium">
          {{ t('orders.orderType') }}
        </NText>

        <NText
          v-if="isOfflineMarketOrder(selectedOrder.orderExecutionType)"
          variant="caption1"
          weight="medium"
          class="pr-2"
        >
          {{ t('orders.twentyFourHourMarketPrice') }}
        </NText>
        <NText v-else variant="caption1" weight="medium" class="pr-2">
          {{ t('orders.twentyFourHourDesiredPrice') }}
        </NText>
      </div>

      <div class="flex justify-between items-center pb-3">
        <NText variant="caption1" weight="medium">
          {{ t('orders.orderSide') }}
        </NText>

        <NText
          v-if="selectedOrder.orderSideId === ORDER_SIDES.buy"
          variant="caption1"
          weight="medium"
          class="pr-2 text-brand-primary"
        >
          {{ t('common.buy') }}
        </NText>

        <NText
          v-if="selectedOrder.orderSideId === ORDER_SIDES.sell"
          variant="caption1"
          weight="medium"
          class="pr-2 text-critical-primary"
        >
          {{ t('common.sell') }}
        </NText>
      </div>

      <div
        class="flex justify-between items-center pb-3 border-b border-neutral-tertiary"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_state') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ selectedOrder.orderState }}
        </NText>
      </div>

      <template v-if="isOfflineMarketOrder(selectedOrder.orderExecutionType)">
        <div class="flex justify-between items-center py-3">
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.order_price') }}
          </NText>

          <NText variant="caption1" weight="medium">
            {{ t('modules.order.priceType.marketPrice.title') }}
          </NText>
        </div>

        <div
          v-if="algorithmAverageTransactionPrice(props.selectedOrder, wage)"
          class="flex justify-between items-center pb-3"
        >
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.AverageTransactionPrice') }}
          </NText>

          <div class="flex flex-row items-center">
            <NumberFormatter
              #="{ number }"
              :value="
                algorithmAverageTransactionPrice(props.selectedOrder, wage)
              "
              round
            >
              <NText variant="caption1" weight="medium" class="pl-1">
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <div class="flex justify-between items-center pb-3">
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.amountOrder') }}
          </NText>

          <div class="flex flex-row items-center">
            <NumberFormatter #="{ number }" :value="priceOrderAmount">
              <NText variant="caption1" weight="medium" class="pl-1">
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText
              v-if="isOrderBasedOnQuantity"
              variant="overline"
              class="text-neutral-secondary"
            >
              {{ t('modules.order.stock') }}
            </NText>
            <NText v-else variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <div
          class="flex justify-between items-center border-b border-neutral-tertiary pb-3"
        >
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.amountDone') }}
          </NText>

          <div class="flex flex-row items-center">
            <NumberFormatter #="{ number }" :value="priceOrderExecutedAmount">
              <NText variant="caption1" weight="medium" class="pl-1">
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText
              v-if="isOrderBasedOnQuantity"
              variant="overline"
              class="text-neutral-secondary"
            >
              {{ t('modules.order.stock') }}
            </NText>
            <NText v-else variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="flex justify-between items-center py-3">
          <NText variant="caption1" weight="medium">
            {{ t('misc.quantity') }}
            ({{ t('modules.order.executed') }})
          </NText>

          <div class="flex flex-row items-center">
            <NumberFormatter
              #="{ number }"
              :value="selectedOrder.executedQuantity"
            >
              <NText variant="caption1" weight="medium" class="pl-1">
                ({{ number }})
              </NText>
            </NumberFormatter>

            <NumberFormatter #="{ number }" :value="selectedOrder.quantity">
              <NText variant="caption1" weight="medium">{{ number }}</NText>
            </NumberFormatter>
          </div>
        </div>

        <div class="flex justify-between items-center pb-3">
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.order_price') }}
          </NText>

          <div>
            <NumberFormatter #="{ number }" :value="selectedOrder.price">
              <NText
                :key="number"
                variant="caption1"
                weight="medium"
                class="mx-1"
              >
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <div
          v-if="algorithmAverageTransactionPrice(props.selectedOrder, wage)"
          class="flex justify-between items-center pb-3"
        >
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.AverageTransactionPrice') }}
          </NText>

          <div class="flex flex-row items-center">
            <NumberFormatter
              #="{ number }"
              :value="
                Number(
                  algorithmAverageTransactionPrice(props.selectedOrder, wage)
                )
              "
              round
            >
              <NText variant="caption1" weight="medium" class="pl-1">
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <div
          class="flex justify-between items-center border-b border-neutral-tertiary pb-3"
        >
          <NText variant="caption1" weight="medium">
            {{ t('modules.order.orderTotalValue') }}
          </NText>

          <NLoading
            v-if="isLoadingSymbol"
            class="text-brand-primary"
            size="10"
          />

          <div v-else>
            <NumberFormatter #="{ number }" :value="totalValue" round>
              <NText
                :key="number"
                variant="caption1"
                weight="medium"
                class="mx-1"
              >
                {{ number }}
              </NText>
            </NumberFormatter>

            <NText variant="overline" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>
      </template>

      <div class="flex justify-between items-center py-3">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.creationTime') }}
        </NText>

        <div>
          <NText variant="caption1" weight="medium" class="pr-2">
            {{ creationTime }}
          </NText>
        </div>
      </div>
      <div class="flex justify-between items-center pb-3">
        <NText variant="caption1" weight="medium">
          {{ t('orders.valid_until') }}
        </NText>

        <NText variant="caption1" weight="medium">
          {{ orderValidityDate }}
        </NText>
      </div>

      <div
        v-if="selectedOrder.errorDescription"
        class="flex justify-between pb-3"
      >
        <NText variant="caption1" weight="medium">
          {{ t('common.description') }}
        </NText>

        <NText variant="caption1" weight="medium" class="mr-4">
          {{ selectedOrder.errorDescription }}
        </NText>
      </div>
    </div>

    <div v-if="isDeletable" class="flex items-center mt-6">
      <NButton
        ref="deleteOrderButtonRef"
        v-gid="{
          module: 'order',
          screen: 'home',
          section: 'offlineOrderDetailDialog',
          component: 'orderCancelButton',
        }"
        variant="outline"
        color="red"
        class="w-full"
        size="small"
        @click="deleteOrderDialog"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.cancelOrder') }}
        </NText>
      </NButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialog, useToast } from '@negar/core';
import { InitialPublicOfferItem, OrderItem } from '@libs/api-reference';
import {
  ORDER_SIDES,
  ORDERS_QUERY_KEYS,
  useDeleteOfflineOrderQuery,
  isDeletableOrder,
  isOfflineMarketOrder,
  algorithmAverageTransactionPrice,
} from '@core/order';
import {
  calcSymbolBuyValue,
  calcSymbolSellValue,
  getSymbolIsin,
  useSymbolDeductions,
} from '@core/symbol';
import { sleep } from '@libs/utils';
import { useQueryClient } from '@libs/use';
import { useRoute } from 'vue-router';

import { DeleteOrderDialogFooter } from '@/modules/order';
import {
  Retail,
  SymbolTradeStateIcon,
  useLiveSymbolQuery,
} from '@/modules/symbol';
import { useIpoDialogs } from '@/modules/ipo';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

type OrderDetailProps = {
  selectedOrder: OrderItem;
  offer?: InitialPublicOfferItem;
};

const props = defineProps<OrderDetailProps>();

const { t, d } = useI18n();

const { add: addDialog } = useDialog();
const toast = useToast();
const queryClient = useQueryClient();
const route = useRoute();

const isin = ref(props.selectedOrder?.instrumentIdentification as string);
const deleteOrderButtonRef = ref<HTMLElement>();

const isOrderBasedOnQuantity = Boolean(props.selectedOrder.quantity);

const { symbol, isLoadingSymbol } = useLiveSymbolQuery(isin);
const { findWageConfigBySymbol } = useSymbolDeductions();

const { openDeleteOfferDialog } = useIpoDialogs();

const { deleteOfflineOrder, isLoadingDeleteOfflineOrder } =
  useDeleteOfflineOrderQuery({
    async onSuccess(result, { closeDeleteOrderDialog }) {
      toast.neutral({
        message: t('modules.order.cancelOrderMessage'),
        duration: 3000,
      });

      await sleep(1000);

      queryClient.invalidateQueries(ORDERS_QUERY_KEYS.orders);

      closeDeleteOrderDialog();
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });

const wage = computed(() => findWageConfigBySymbol(unref(symbol)));

const calcTotalValue = {
  [ORDER_SIDES.buy]: calcSymbolBuyValue,
  [ORDER_SIDES.sell]: calcSymbolSellValue,
};

const priceOrderAmount = computed(() => {
  if (isOrderBasedOnQuantity) {
    return props.selectedOrder.quantity;
  }

  return props.selectedOrder.investValue;
});

const priceOrderExecutedAmount = computed(() => {
  if (isOrderBasedOnQuantity) {
    return props.selectedOrder.executedQuantity;
  }

  return props.selectedOrder.executedValue;
});

const totalValue = computed(() => {
  return calcTotalValue[props.selectedOrder.orderSideId](
    {
      quantity: Number(props.selectedOrder?.quantity),
      price: Number(props.selectedOrder?.price),
    },
    unref(wage)
  );
});

const creationTime = computed(
  () => `${d(props.selectedOrder.entryDate, 'shortDate')} ,
    ${d(props.selectedOrder.entryDate, 'shortTime')}`
);

const deleteOrderTitle = computed(
  () =>
    `${t('modules.order.cancelOrder')} ${
      props.selectedOrder?.orderSideId === ORDER_SIDES.buy
        ? t('common.buy')
        : t('common.sell')
    } ${props.selectedOrder?.instrumentName}`
);

const deleteOrderDialog = () => {
  if (props.offer) {
    openDeleteOfferDialog({
      offer: props.offer,
      orderId: props.selectedOrder.orderId,
    });
  } else {
    addDialog({
      bodyClass: '!w-1/5',
      title: deleteOrderTitle.value,
      content: t('modules.order.cancelOrderQuestion'),
      referenceElementRef: deleteOrderButtonRef,
      placement: 'top',
      footer: computed(() => ({
        component: DeleteOrderDialogFooter,
        props: {
          order: props.selectedOrder,
          loadingDeleteOrder: isLoadingDeleteOfflineOrder.value,
          onDeleteOrder: deleteOfflineOrder,
        },
      })),
    });
  }
};

const orderValidityDate = computed(() =>
  props.selectedOrder.validityDate
    ? d(props.selectedOrder.validityDate, 'shortDate')
    : ''
);

const isFromHistoryPath = route.path.includes('history');

const isDeletable = computed(() =>
  isDeletableOrder(props.selectedOrder, isFromHistoryPath)
);
</script>
